/**
 * Shared presentation_rules shape (ADR-005 / PRES-001).
 * Keep aligned with Offers::PresentationRules on the API.
 */

export const PRESENTATION_FIELD_KEYS = Object.freeze([
  "description",
  "quantity",
  "unit",
  "unit_price",
  "product_image",
  "product_url",
]);

export const DEFAULT_SHOW_FIELDS = Object.freeze({
  description: true,
  quantity: true,
  unit: true,
  unit_price: true,
  product_image: true,
  product_url: true,
});

export function defaultPresentationRules() {
  return {
    version: 1,
    defaults: {
      group: "show_children",
      nested_calculation: "sum_only",
      show_product_info: true,
      include_product_short_description: false,
      include_notes: true,
      show_fields: { ...DEFAULT_SHOW_FIELDS },
    },
    rules: [],
  };
}

function sanitizeMode(value, fallback) {
  return ["show_children", "sum_only", "hide"].includes(value)
    ? value
    : fallback;
}

function sanitizeFields(incoming) {
  const next = { ...DEFAULT_SHOW_FIELDS };
  if (!incoming || typeof incoming !== "object") return next;
  PRESENTATION_FIELD_KEYS.forEach((key) => {
    if (incoming[key] !== undefined) next[key] = Boolean(incoming[key]);
  });
  return next;
}

function sanitizeHiddenFields(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((key) => PRESENTATION_FIELD_KEYS.includes(key));
}

export function normalizePresentationRules(incoming) {
  if (!incoming || typeof incoming !== "object") {
    return defaultPresentationRules();
  }
  const defaultsIn = incoming.defaults || {};
  return {
    version: 1,
    defaults: {
      group: sanitizeMode(defaultsIn.group, "show_children"),
      nested_calculation: sanitizeMode(
        defaultsIn.nested_calculation,
        "sum_only"
      ),
      show_product_info:
        defaultsIn.show_product_info === undefined
          ? true
          : Boolean(defaultsIn.show_product_info),
      include_product_short_description: Boolean(
        defaultsIn.include_product_short_description
      ),
      include_notes:
        defaultsIn.include_notes === undefined
          ? true
          : Boolean(defaultsIn.include_notes),
      show_fields: sanitizeFields(defaultsIn.show_fields),
    },
    rules: Array.isArray(incoming.rules)
      ? incoming.rules.map((rule) => ({
          ...rule,
          hidden_fields: sanitizeHiddenFields(rule?.hidden_fields),
        }))
      : [],
  };
}

export function itemNodeKey(item) {
  if (!item || typeof item !== "object") return null;
  if (item.source_calculation_item_id != null) {
    return `item:${item.source_calculation_item_id}`;
  }
  if (item.id != null && !String(item.id).startsWith("gen-")) {
    return `item:${item.id}`;
  }
  return null;
}

export function ruleForNode(rules, nodeKey) {
  if (!nodeKey) return null;
  return (rules?.rules || []).find((rule) => rule.node_key === nodeKey) || null;
}

export function isFieldVisible(rules, fieldKey, nodeKey) {
  const normalized = normalizePresentationRules(rules);
  const shown = normalized.defaults.show_fields?.[fieldKey];
  if (shown === false) return false;
  const hidden = ruleForNode(normalized, nodeKey)?.hidden_fields || [];
  return !hidden.includes(fieldKey);
}

export function notesIncluded(rules) {
  return normalizePresentationRules(rules).defaults.include_notes !== false;
}
